import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, Loading, Empty, Th, Td, TableWrap, NativeSelect } from "@/components/kit";
import { dmy, errMsg } from "@/lib/format";

export default function Members() {
  const { api, t, can, company } = useApp();
  const [d, setD] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("sales");
  const load = useCallback(() => api.get("/members").then((r) => setD(r.data)), [api]);
  useEffect(() => { load(); }, [load]);
  const rw = can("members", true);
  const run = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); load(); } catch (e) { toast.error(errMsg(e)); } };
  const invite = (e) => {
    e.preventDefault();
    run(async () => {
      const { data } = await api.post("/invitations", { email, role });
      setEmail("");
      if (!data.email_sent) toast.warning("Invitation créée mais l'email n'a pas pu être envoyé");
    }, t("invite"));
  };
  const roles = (d?.roles || []).filter((r) => r !== "owner" || company.role === "owner");
  return (
    <>
      <PageHeader eyebrow={company.name} title={t("members")} />
      {rw && (
        <Panel title={t("invite")}>
          <form onSubmit={invite} className="flex flex-wrap gap-3">
            <Input type="email" required placeholder="collaborateur@societe.tn" value={email} onChange={(e) => setEmail(e.target.value)} className="w-72" data-testid="invite-email" />
            <NativeSelect value={role} onChange={(e) => setRole(e.target.value)} data-testid="invite-role">{roles.map((r) => <option key={r} value={r}>{t(`r_${r}`)}</option>)}</NativeSelect>
            <Button type="submit" data-testid="invite-submit"><UserPlus className="h-4 w-4 me-1" />{t("invite")}</Button>
          </form>
          <p className="mt-2 text-xs text-slate-500">{t("users")} : {(d?.members.length || 0) + (d?.invitations.length || 0)} / {company.plan?.max_users}</p>
        </Panel>
      )}
      <Panel>
        {!d ? <Loading /> : (
          <TableWrap>
            <thead className="border-b"><tr><Th>{t("name")}</Th><Th>{t("email")}</Th><Th>{t("role")}</Th><Th>{t("date")}</Th><Th /></tr></thead>
            <tbody>{d.members.map((m) => (
              <tr key={m.user_id} className="border-b border-slate-50" data-testid={`member-row-${m.email}`}>
                <Td className="font-medium">{m.name}</Td><Td>{m.email}</Td>
                <Td>{rw && (company.role === "owner" || m.role !== "owner") ? (
                  <NativeSelect value={m.role} onChange={(e) => run(() => api.patch(`/members/${m.user_id}`, { role: e.target.value }), t("role"))} data-testid={`member-role-${m.email}`}>{roles.concat(m.role === "owner" && !roles.includes("owner") ? ["owner"] : []).map((r) => <option key={r} value={r}>{t(`r_${r}`)}</option>)}</NativeSelect>
                ) : t(`r_${m.role}`)}</Td>
                <Td className="num text-xs">{dmy(m.joined_at)}</Td>
                <Td className="text-end">{rw && m.role !== "owner" && <Button variant="ghost" size="icon" onClick={() => window.confirm(t("remove")) && run(() => api.delete(`/members/${m.user_id}`))} data-testid={`member-remove-${m.email}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}</Td>
              </tr>))}</tbody>
          </TableWrap>
        )}
      </Panel>
      <Panel title={t("invitations")}>
        {!d || d.invitations.length === 0 ? <Empty /> : d.invitations.map((i) => (
          <div key={i.id} className="flex items-center gap-3 border-b border-slate-50 py-2 text-sm" data-testid={`invitation-row-${i.email}`}>
            <span>{i.email}</span><span className="text-xs text-slate-500">{t(`r_${i.role}`)} · {dmy(i.created_at)}</span>
            {rw && <Button size="sm" variant="ghost" className="ms-auto" onClick={() => run(() => api.delete(`/invitations/${i.id}`))} data-testid={`invitation-revoke-${i.email}`}><X className="h-4 w-4 me-1" />{t("revoke")}</Button>}
          </div>
        ))}
      </Panel>
    </>
  );
}
